import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSubscription } from '@/hooks/useSubscription';

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refetch } = useSubscription();
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    // Refetch subscription to get updated status
    refetch();

    // Auto-redirect countdown
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/app');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, refetch]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/20 flex items-center justify-center"
        >
          <CheckCircle className="w-10 h-10 text-primary" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h1 className="text-3xl font-bold mb-2">Welcome to Pro!</h1>
          <div className="flex items-center justify-center gap-2 text-primary mb-4">
            <Sparkles className="w-5 h-5" />
            <span className="font-medium">Your upgrade was successful</span>
            <Sparkles className="w-5 h-5" />
          </div>
          
          <p className="text-muted-foreground mb-8">
            You now have access to unlimited history, advanced insights, 
            unlimited tags, cloud sync, and weekly summaries.
          </p>

          <Button 
            onClick={() => navigate('/app')} 
            size="lg"
            className="w-full mb-4"
          >
            Start Using Luma Pro
          </Button>

          <p className="text-sm text-muted-foreground">
            Redirecting automatically in {countdown}s...
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}
